import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Info, AlertCircle } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────────
 * Redeem Gift Card Credits modal (rebuilt June 2026 per the My Rewards
 * UX spec).
 *
 * Highlights of this iteration:
 *   • Three-tile balance preview: Available · Requesting Now · Remaining
 *     After Request — updates live as the user types/clicks a quick
 *     action button. "Requested" was renamed to "Requesting Now" to
 *     remove the past-tense ambiguity (it now clearly refers to the
 *     in-progress amount).
 *   • Quick action buttons (Redeem All, $5, $10, $20, $25). Each preset
 *     auto-populates the amount field. Presets that exceed Available
 *     render disabled so users can't pick an impossible amount.
 *   • Inline red error banner ("Requested amount exceeds your
 *     available Gift Card Credits.") replaces the silent disabled
 *     button — the user always knows WHY they can't submit.
 *   • Redemption Summary block (between notes + footer) restates the
 *     final ask in plain language right before the user clicks Submit.
 *   • Wording is strictly "Gift Card Credits". No "cash", "withdrawal",
 *     "payout" or "money transfer" copy anywhere — these are
 *     promotional credits redeemable at participating NoFee restaurants.
 *
 * Submits to `POST /customers/me/redemption-requests`, which atomically
 * moves the requested cents from `gift_credit_cents` →
 * `pending_redemption_cents`. See backend/redemption_requests.py for
 * the manual-review state machine.
 * ─────────────────────────────────────────────────────────────────── */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const PRESETS_CENTS = [500, 1000, 2000, 2500];  // $5 · $10 · $20 · $25

const RedeemGiftCardForm = ({
  token,
  availableCents,
  pendingCents: _pendingCents,
  points: _points,
  ptsPerDollar: _ptsPerDollar,
  processingHours,
  defaultEmail,
  defaultName,
  defaultPhone,
  onClose,
  onSubmitted,
}) => {
  const [fullName, setFullName] = useState(defaultName || '');
  const [email, setEmail] = useState(defaultEmail || '');
  const [phone, setPhone] = useState(defaultPhone || '');
  const [restaurantId, setRestaurantId] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [requestedDollars, setRequestedDollars] = useState('');
  const [notes, setNotes] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Customer-facing list — restaurant selection is OPTIONAL ("for use
    // at" preference, not a hard binding). Limit to 60 so the dropdown
    // stays manageable. Public endpoint, no auth required.
    axios
      .get(`${API}/restaurants?limit=60`)
      .then((r) => setRestaurants(Array.isArray(r.data) ? r.data : (r.data?.restaurants || [])))
      .catch(() => setRestaurants([]));
  }, []);

  const requestedCents = Math.max(0, Math.round(parseFloat(requestedDollars || '0') * 100));
  const remainingAfter = Math.max(0, availableCents - requestedCents);
  const exceedsAvailable = requestedCents > availableCents;
  const belowMinimum = requestedCents > 0 && requestedCents < 100;
  const noAmount = requestedCents === 0;
  const invalidAmount = exceedsAvailable || belowMinimum || noAmount;
  const contactMissing = !fullName.trim() || !email.trim() || !phone.trim();

  const setPresetDollars = (cents) => {
    // Format with no decimal if it's a whole dollar (so the input
    // shows "5" instead of "5.00" — cleaner UX).
    const dollars = cents / 100;
    setRequestedDollars(Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (invalidAmount || contactMissing) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/customers/me/redemption-requests`,
        {
          full_name: fullName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          requested_cents: requestedCents,
          restaurant_id: restaurantId || null,
          restaurant_name: restaurantName || null,
          notes: notes.trim() || '',
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Redemption request submitted. We'll review within ${processingHours || 72} hours.`);
      onSubmitted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Submission failed';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="redeem-modal"
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg bg-zinc-950 border border-yellow-500/25 rounded-3xl shadow-2xl my-auto"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Redeem</div>
            <h2 className="text-lg font-extrabold text-white">Gift Card Credits</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="redeem-close-btn"
            className="p-1.5 rounded-full hover:bg-white/5"
          >
            <X className="w-4 h-4 text-white/70" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3.5">
          {/* ─── Live balance preview (3 tiles) ─── */}
          <div
            data-testid="redeem-balance-preview"
            className="grid grid-cols-3 gap-2.5 p-3 rounded-2xl bg-black/40 border border-white/10"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">Available</div>
              <div className="mt-0.5 text-base font-extrabold text-white tabular-nums" data-testid="redeem-available">
                {money(availableCents)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-yellow-300/85 font-bold">Requesting Now</div>
              <div
                className={`mt-0.5 text-base font-extrabold tabular-nums ${exceedsAvailable ? 'text-rose-300' : 'text-yellow-300'}`}
                data-testid="redeem-requesting-now"
              >
                {money(requestedCents)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/85 font-bold">Remaining After Request</div>
              <div className="mt-0.5 text-base font-extrabold text-emerald-300 tabular-nums" data-testid="redeem-remaining-preview">
                {money(remainingAfter)}
              </div>
            </div>
          </div>

          {/* ─── Quick action buttons ─── */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Quick amounts</div>
            <div data-testid="redeem-quick-actions" className="grid grid-cols-5 gap-1.5">
              <button
                type="button"
                onClick={() => setPresetDollars(availableCents)}
                disabled={availableCents < 100}
                data-testid="redeem-quick-all"
                className="px-2 py-2 rounded-lg border border-yellow-500/40 bg-yellow-500/[0.06] text-yellow-300 text-[11.5px] font-extrabold uppercase tracking-wide hover:bg-yellow-500/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Redeem All
              </button>
              {PRESETS_CENTS.map((cents) => {
                const tooMuch = cents > availableCents;
                const active = requestedCents === cents && !tooMuch;
                return (
                  <button
                    key={cents}
                    type="button"
                    onClick={() => setPresetDollars(cents)}
                    disabled={tooMuch}
                    data-testid={`redeem-quick-${cents / 100}`}
                    className={`px-2 py-2 rounded-lg border text-[12.5px] font-extrabold tabular-nums transition-colors ${
                      active
                        ? 'border-yellow-400 bg-yellow-500/[0.18] text-yellow-200'
                        : tooMuch
                          ? 'border-white/8 bg-white/[0.02] text-white/30 cursor-not-allowed'
                          : 'border-white/15 bg-white/[0.03] text-white/85 hover:border-yellow-500/40 hover:bg-yellow-500/[0.06]'
                    }`}
                  >
                    ${cents / 100}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ─── Amount input ─── */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1">
              Request amount ($)
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.50"
              min="1"
              max={(availableCents / 100).toFixed(2)}
              value={requestedDollars}
              onChange={(e) => setRequestedDollars(e.target.value)}
              data-testid="redeem-input-amount"
              className={`w-full px-3 py-2.5 rounded-xl bg-black/40 border text-white text-[14px] outline-none ${
                exceedsAvailable || belowMinimum
                  ? 'border-rose-500/50 focus:border-rose-400'
                  : 'border-white/10 focus:border-yellow-500/50'
              }`}
              placeholder="10.00"
              required
            />
            <p className="mt-1 text-[11px] text-white/45">
              Available {money(availableCents)} · minimum $1.00
            </p>

            {/* Inline error banner — visible only when the typed value
                actually breaks a rule. Empty input shows no error so the
                form doesn't yell at users mid-typing. */}
            {exceedsAvailable && (
              <div
                data-testid="redeem-error-exceeds"
                className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/[0.08] text-[12.5px] text-rose-200"
              >
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Requested amount exceeds your available Gift Card Credits.</span>
              </div>
            )}
            {belowMinimum && (
              <div
                data-testid="redeem-error-below-min"
                className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/[0.08] text-[12.5px] text-rose-200"
              >
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Minimum request is $1.00.</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1">Full name</label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              data-testid="redeem-input-name"
              className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-white text-[14px] focus:border-yellow-500/50 outline-none"
              placeholder="Jane Doe"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="redeem-input-email"
                className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-white text-[14px] focus:border-yellow-500/50 outline-none"
                placeholder="jane@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="redeem-input-phone"
                className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-white text-[14px] focus:border-yellow-500/50 outline-none"
                placeholder="+1 (555) 555-5555"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1">
              Preferred restaurant <span className="text-white/40">(optional)</span>
            </label>
            <div className="relative">
              <select
                value={restaurantId}
                onChange={(e) => {
                  const id = e.target.value;
                  setRestaurantId(id);
                  const found = restaurants.find((r) => r.id === id);
                  setRestaurantName(found?.name || '');
                }}
                data-testid="redeem-input-restaurant"
                className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-white text-[14px] focus:border-yellow-500/50 outline-none appearance-none pr-9 cursor-pointer"
              >
                <option value="">— No preference (use anywhere) —</option>
                {restaurants.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-white/40">▾</div>
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1">
              Notes <span className="text-white/40">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              data-testid="redeem-input-notes"
              className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-white text-[14px] focus:border-yellow-500/50 outline-none resize-none"
              placeholder="Anything we should know?"
            />
          </div>

          {/* ─── Redemption Summary (shown once the user has picked a
              valid amount; restates the ask in plain English right
              before they click Submit) ─── */}
          {!invalidAmount && (
            <div
              data-testid="redeem-summary"
              className="rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.05] p-4"
            >
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold mb-2">
                Redemption Summary
              </div>
              <dl className="space-y-1.5 text-[13px]">
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-white/65">You are requesting</dt>
                  <dd className="text-yellow-300 font-extrabold tabular-nums" data-testid="redeem-summary-requesting">
                    {money(requestedCents)} Gift Card Credits
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-white/65">Remaining after approval</dt>
                  <dd className="text-emerald-300 font-extrabold tabular-nums" data-testid="redeem-summary-remaining">
                    {money(remainingAfter)} Gift Card Credits
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-white/65">Review time</dt>
                  <dd className="text-white font-semibold" data-testid="redeem-summary-review-time">
                    Up to {processingHours || 72} hours
                  </dd>
                </div>
              </dl>
            </div>
          )}

          <p className="text-[12px] text-white/55 flex items-start gap-1.5 leading-relaxed">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-yellow-300/85" />
            Once submitted, the amount moves to <strong className="text-amber-300">Pending Review</strong>. Credits return to your Available balance automatically if the request is rejected.
          </p>
        </div>

        <div className="px-5 py-4 border-t border-white/10 flex flex-col sm:flex-row gap-2.5">
          <button
            type="button"
            onClick={onClose}
            data-testid="redeem-cancel-btn"
            className="flex-1 px-4 py-3 rounded-xl border border-white/15 text-white/80 font-semibold text-[13.5px] hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || invalidAmount || contactMissing}
            data-testid="redeem-submit-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[13.5px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : 'Submit request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RedeemGiftCardForm;
